import React from 'react';

import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { Upload, message } from 'antd';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

// 與後端 OrderAttachment 驗證一致：只允許 JPEG / PNG / PDF
export const ACCEPTABLE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

interface OrderAttachmentsUploadProps {
  value?: File[];
  onChange?: (files: File[]) => void;
}

/**
 * 建單表單的附件選擇區（拖拉上傳框）。
 * 建單當下訂單還不存在、沒有 order id，無法即時上傳，因此這裡只把檔案
 * 暫存在 state，等訂單建立成功後由 OrderNew 逐筆呼叫 createAttachment 上傳。
 * 選檔時即時擋掉非 JPEG / PNG / PDF，與後端驗證一致，避免建單後才靜默失敗。
 */
const OrderAttachmentsUpload: React.FC<OrderAttachmentsUploadProps> = ({ value = [], onChange }) => {
  const { t } = useTranslation();

  const handleBeforeUpload = (file: File) => {
    if (!ACCEPTABLE_CONTENT_TYPES.includes(file.type)) {
      message.error(t('shared.orders.attachments.invalid_type'));
      return Upload.LIST_IGNORE;
    }
    onChange?.([...value, file]);
    return false; // 不自動上傳，等建單成功後再傳
  };

  const handleRemove = (index: number) => {
    onChange?.(value.filter((_, i) => i !== index));
  };

  return (
    <div>
      {value.length > 0 && (
        <div style={{ marginBottom: 8, fontSize: 12 }}>
          {value.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: '1px solid #f5f5f5',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </span>
              <DeleteOutlined
                style={{ color: '#ff4d4f', cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}
                onClick={() => handleRemove(index)}
              />
            </div>
          ))}
        </div>
      )}

      <Dragger showUploadList={false} beforeUpload={handleBeforeUpload} multiple>
        <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
          <InboxOutlined />
        </p>
        <p style={{ fontSize: 11, color: '#aaa', margin: 0 }}>
          {t('shared.orders.attachments.upload_hint')}
        </p>
        <p style={{ fontSize: 11, color: '#1677ff', margin: 0 }}>
          {t('shared.orders.attachments.upload_or_click')}
        </p>
      </Dragger>
    </div>
  );
};

export default OrderAttachmentsUpload;
